import {
  User,
  type ResolveTenantResponse,
  type ResolvedTenant,
} from "./authService";
import config from "../../../config";

class AuthStorageService {
  private readonly STORAGE_KEYS = {
    JWT_TOKEN: config.STORAGE_KEYS.JWT_TOKEN,
    USER_DATA: config.STORAGE_KEYS.USER_DATA,
    TENANT_ID: config.STORAGE_KEYS.TENANT_ID,
    TENANT_META: config.STORAGE_KEYS.TENANT_META,
    FORCE_LOGOUT: config.STORAGE_KEYS.FORCE_LOGOUT,
  };

  /**
   * Get stored JWT / Cognito access token
   */
  getToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.JWT_TOKEN);
  }

  /**
   * Set JWT / Cognito access token
   */
  setToken(token: string): void {
    localStorage.setItem(this.STORAGE_KEYS.JWT_TOKEN, token);
  }

  /**
   * Remove JWT / Cognito access token
   */
  removeToken(): void {
    localStorage.removeItem(this.STORAGE_KEYS.JWT_TOKEN);
  }

  /** x-tenantid value, e.g. mira_10001 */
  getTenantId(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.TENANT_ID);
  }

  setTenantId(tenantId: string): void {
    localStorage.setItem(this.STORAGE_KEYS.TENANT_ID, tenantId);
  }

  removeTenantId(): void {
    localStorage.removeItem(this.STORAGE_KEYS.TENANT_ID);
  }

  getTenantMeta(): ResolveTenantResponse | null {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEYS.TENANT_META);
      return raw ? (JSON.parse(raw) as ResolveTenantResponse) : null;
    } catch {
      return null;
    }
  }

  setTenantMeta(meta: ResolveTenantResponse): void {
    localStorage.setItem(this.STORAGE_KEYS.TENANT_META, JSON.stringify(meta));
  }

  removeTenantMeta(): void {
    localStorage.removeItem(this.STORAGE_KEYS.TENANT_META);
  }

  getTenantName(): string | null {
    return this.getTenantMeta()?.tenantName ?? null;
  }

  /** Cached tenant from localStorage when fully resolved. */
  getCachedResolvedTenant(): ResolvedTenant | null {
    const existingId = this.getTenantId();
    const existingMeta = this.getTenantMeta();
    if (
      existingId?.startsWith("mira_") &&
      existingMeta?.tenantCode &&
      existingMeta?.tenantName
    ) {
      return {
        tenantId: existingId,
        tenantCode: existingMeta.tenantCode,
        tenantName: existingMeta.tenantName,
        uiUrl: existingMeta.uiUrl,
        meta: existingMeta,
      };
    }
    return null;
  }

  persistResolvedTenant(resolved: ResolvedTenant): ResolvedTenant {
    this.setTenantId(resolved.tenantId);
    this.setTenantMeta(resolved.meta);
    return resolved;
  }

  /**
   * Prevent Cognito session restore from bouncing back into the app after 401/logout.
   */
  markForceLogout(): void {
    sessionStorage.setItem(this.STORAGE_KEYS.FORCE_LOGOUT, "1");
  }

  consumeForceLogout(): boolean {
    const flagged = sessionStorage.getItem(this.STORAGE_KEYS.FORCE_LOGOUT) === "1";
    if (flagged) {
      sessionStorage.removeItem(this.STORAGE_KEYS.FORCE_LOGOUT);
    }
    return flagged;
  }

  /**
   * Get current user data
   */
  getCurrentUser(): User | null {
    try {
      const userData = localStorage.getItem(this.STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }

  /**
   * Set user data
   */
  setUser(user: User): void {
    localStorage.setItem(this.STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  }

  /**
   * Remove user data
   */
  removeUser(): void {
    localStorage.removeItem(this.STORAGE_KEYS.USER_DATA);
  }

  /**
   * Check if user is authenticated (token present).
   * Tenant is resolved separately and attached as x-tenantid.
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Clear all auth data
   */
  clear(): void {
    this.removeToken();
    this.removeUser();
    this.removeTenantId();
    this.removeTenantMeta();
  }

  /**
   * Get user role
   */
  getUserRole(): string | null {
    const user = this.getCurrentUser();
    return user?.role || null;
  }

  /**
   * Get user roles array
   */
  getUserRoles(): string[] {
    const user = this.getCurrentUser();
    return user?.roles || [];
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const roles = this.getUserRoles();
    return roles.includes(role);
  }

  /**
   * Get user ID
   */
  getUserId(): number | string | null {
    const user = this.getCurrentUser();
    return user?.id || user?._id || null;
  }
}

export const authStorageService = new AuthStorageService();
export default authStorageService;
